import React, { useEffect } from "react";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { useProgress } from "@/context/ProgressContext";

export function LessonChecklist({ lessonId, items }) {
  const { colors } = useTheme();
  const { isDone, toggle } = useProgress();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
      {items.map((text, i) => {
        const key = `${lessonId}:c${i}`;
        const checked = isDone(key);
        return (
          <button
            key={key}
            data-testid={`checklist-item-${i}`}
            className="pressable"
            onClick={() => toggle(key)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              background: colors.surfaceSecondary,
              border: `1px solid ${colors.border}`,
              borderRadius: 14,
              padding: 14,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                border: `2px solid ${checked ? colors.success : colors.borderStrong}`,
                background: checked ? colors.success : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {checked ? <Icon name="check" size={15} color={colors.onSuccess} /> : null}
            </div>
            <span style={{ flex: 1, color: checked ? colors.muted : colors.onSurface, fontSize: 15, fontFamily: fonts.text, lineHeight: "21px" }}>{text}</span>
          </button>
        );
      })}
    </div>
  );
}

export function LessonQuiz({ questions, answers, onSelect, onResult }) {
  const { colors } = useTheme();
  const correctCount = questions.filter((q) => answers[q.id] === q.answer).length;
  const passed = correctCount === questions.length;

  useEffect(() => {
    onResult(passed);
  }, [passed, onResult]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 4 }}>
      <div data-testid="quiz-score" style={{ color: colors.brand, fontSize: 14, fontFamily: fonts.text, fontWeight: 800 }}>
        {correctCount} / {questions.length} correct
      </div>
      {questions.map((q, qi) => {
        const selected = answers[q.id];
        const answered = selected !== undefined;
        return (
          <div key={q.id} data-testid={`quiz-question-${qi}`} style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ color: colors.onSurface, fontSize: 16, fontFamily: fonts.text, fontWeight: 700, lineHeight: "22px" }}>
              {qi + 1}. {q.q}
            </div>
            {q.options.map((opt, oi) => {
              const isSelected = selected === oi;
              const showCorrect = answered && oi === q.answer;
              const showWrong = answered && isSelected && oi !== q.answer;
              let borderColor = colors.border;
              if (showCorrect) borderColor = colors.success;
              if (showWrong) borderColor = colors.error;
              return (
                <button
                  key={oi}
                  data-testid={`quiz-${q.id}-option-${oi}`}
                  className="pressable"
                  onClick={() => onSelect(q.id, oi)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    background: showCorrect || showWrong ? colors.surfaceTertiary : colors.surface,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 12,
                    padding: "12px 14px",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span style={{ flex: 1, color: colors.onSurface, fontSize: 15, fontFamily: fonts.text, fontWeight: showCorrect || showWrong ? 700 : 400 }}>{opt}</span>
                  {showCorrect ? <Icon name="check" size={16} color={colors.success} /> : showWrong ? <Icon name="x" size={16} color={colors.error} /> : null}
                </button>
              );
            })}
            {answered && selected !== q.answer ? (
              <div style={{ color: colors.muted, fontSize: 13, fontFamily: fonts.text, lineHeight: "19px", fontStyle: "italic" }}>{q.explain}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
